const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
const SLIDER_MIN = 0;
const SLIDER_MAX = 100;
const DEFAULT_SLIDER_SNAP_THRESHOLD = 2.5;

export const TIMELINE_PRESETS = [
  {
    id: "6h",
    label: "6H",
    durationMs: 6 * HOUR,
    assignmentSnap: { unit: "minute", step: 5, mode: "nearest" },
    clusterBucket: { unit: "minute", step: 15, mode: "floor" },
    majorUnit: "hour",
    majorStep: 1,
    majorFormat: "h a",
    majorLabelMode: "major",
    majorLabelMinGapPx: 84,
    minorUnit: "minute",
    minorStep: 15,
    minorTickMinGapPx: 10,
    dividerUnit: "day",
    minSectionPct: 1.5,
  },
  {
    id: "1d",
    label: "1D",
    durationMs: DAY,
    assignmentSnap: { unit: "minute", step: 15, mode: "nearest" },
    clusterBucket: { unit: "minute", step: 30, mode: "floor" },
    majorUnit: "hour",
    majorStep: 2,
    majorFormat: "h a",
    majorLabelMode: "major",
    majorLabelMinGapPx: 88,
    minorUnit: "minute",
    minorStep: 30,
    minorTickMinGapPx: 10,
    dividerUnit: "day",
    minSectionPct: 1.5,
  },
  {
    id: "3d",
    label: "3D",
    durationMs: 3 * DAY,
    assignmentSnap: { unit: "minute", step: 30, mode: "nearest" },
    clusterBucket: { unit: "hour", step: 1, mode: "floor" },
    majorUnit: "hour",
    majorStep: 6,
    majorFormat: "h a",
    majorLabelMode: "major",
    majorLabelMinGapPx: 92,
    minorUnit: "hour",
    minorStep: 1,
    minorTickMinGapPx: 10,
    dividerUnit: "day",
    minSectionPct: 1.5,
  },
  {
    id: "1w",
    label: "1W",
    durationMs: 7 * DAY,
    assignmentSnap: { unit: "hour", step: 1, mode: "nearest" },
    clusterBucket: { unit: "hour", step: 3, mode: "floor" },
    majorUnit: "day",
    majorStep: 1,
    majorFormat: "EEE d",
    majorLabelMode: "none",
    majorLabelMinGapPx: 88,
    minorUnit: "hour",
    minorStep: 6,
    minorTickMinGapPx: 12,
    dividerUnit: "day",
    minSectionPct: 1.5,
  },
  {
    id: "1m",
    label: "1M",
    durationMs: 30 * DAY,
    assignmentSnap: { unit: "hour", step: 6, mode: "nearest" },
    clusterBucket: { unit: "day", step: 1, mode: "floor" },
    majorUnit: "week",
    majorStep: 1,
    majorFormat: "MMM d",
    majorLabelMode: "major",
    majorLabelMinGapPx: 72,
    minorUnit: "day",
    minorStep: 1,
    minorTickMinGapPx: 8,
    dividerUnit: "month",
    minSectionPct: 1.5,
  },
  {
    id: "3m",
    label: "3M",
    durationMs: 90 * DAY,
    assignmentSnap: { unit: "hour", step: 12, mode: "nearest" },
    clusterBucket: { unit: "day", step: 1, mode: "floor" },
    majorUnit: "month",
    majorStep: 1,
    majorFormat: "MMM",
    majorLabelMode: "none",
    majorLabelMinGapPx: 88,
    minorUnit: "week",
    minorStep: 1,
    minorTickMinGapPx: 8,
    dividerUnit: "month",
    minSectionPct: 1.5,
  },
  {
    id: "6m",
    label: "6M",
    durationMs: 180 * DAY,
    assignmentSnap: { unit: "day", step: 1, mode: "nearest" },
    clusterBucket: { unit: "day", step: 2, mode: "floor" },
    majorUnit: "month",
    majorStep: 1,
    majorFormat: "MMM",
    majorLabelMode: "none",
    majorLabelMinGapPx: 92,
    minorUnit: "week",
    minorStep: 1,
    minorTickMinGapPx: 6,
    dividerUnit: "month",
    minSectionPct: 1.5,
  },
  {
    id: "1y",
    label: "1Y",
    durationMs: 365 * DAY,
    assignmentSnap: { unit: "day", step: 1, mode: "nearest" },
    clusterBucket: { unit: "week", step: 1, mode: "floor" },
    majorUnit: "month",
    majorStep: 1,
    majorFormat: "MMM",
    majorLabelMode: "major",
    majorLabelMinGapPx: 68,
    minorUnit: "week",
    minorStep: 2,
    minorTickMinGapPx: 4,
    dividerUnit: "year",
    minSectionPct: 1.5,
  },
];

export const MIN_DURATION_MS = TIMELINE_PRESETS[0].durationMs;
export const MAX_DURATION_MS = TIMELINE_PRESETS[TIMELINE_PRESETS.length - 1].durationMs;
const PRESET_SEGMENT_COUNT = Math.max(1, TIMELINE_PRESETS.length - 1);

export function getPresetById(id) {
  return TIMELINE_PRESETS.find((preset) => preset.id === id) || TIMELINE_PRESETS[0];
}

export function getPresetForDuration(durationMs) {
  return (
    TIMELINE_PRESETS.find((preset) => durationMs <= preset.durationMs) ||
    TIMELINE_PRESETS[TIMELINE_PRESETS.length - 1]
  );
}

export function clampDuration(durationMs) {
  return Math.max(MIN_DURATION_MS, Math.min(MAX_DURATION_MS, durationMs));
}

export function clampSliderValue(value) {
  return Math.max(SLIDER_MIN, Math.min(SLIDER_MAX, value));
}

export function centerRangeAt(date, durationMs) {
  const centered = date.getTime();
  const half = durationMs / 2;
  return {
    start: new Date(centered - half),
    end: new Date(centered + half),
  };
}

export function rangeDurationMs(range) {
  return range.end.getTime() - range.start.getTime();
}

export function durationToSliderValue(durationMs) {
  const clampedDuration = clampDuration(durationMs);

  if (clampedDuration <= MIN_DURATION_MS) return SLIDER_MIN;
  if (clampedDuration >= MAX_DURATION_MS) return SLIDER_MAX;

  let normalized = 1;
  for (let index = 0; index < TIMELINE_PRESETS.length - 1; index += 1) {
    const current = TIMELINE_PRESETS[index].durationMs;
    const next = TIMELINE_PRESETS[index + 1].durationMs;
    if (clampedDuration > next) continue;

    const segmentStart = index / PRESET_SEGMENT_COUNT;
    const segmentSpan = 1 / PRESET_SEGMENT_COUNT;
    const currentLog = Math.log(current);
    const nextLog = Math.log(next);
    const logSpan = Math.max(1e-9, nextLog - currentLog);
    const localT = (Math.log(clampedDuration) - currentLog) / logSpan;
    normalized = segmentStart + Math.max(0, Math.min(1, localT)) * segmentSpan;
    break;
  }

  return clampSliderValue(SLIDER_MIN + normalized * (SLIDER_MAX - SLIDER_MIN));
}

export function sliderValueToDuration(value) {
  const normalized = (clampSliderValue(value) - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN || 1);
  if (normalized <= 0) return MIN_DURATION_MS;
  if (normalized >= 1) return MAX_DURATION_MS;

  const scaled = normalized * PRESET_SEGMENT_COUNT;
  const segmentIndex = Math.min(PRESET_SEGMENT_COUNT - 1, Math.floor(scaled));
  const localT = scaled - segmentIndex;
  const current = TIMELINE_PRESETS[segmentIndex].durationMs;
  const next = TIMELINE_PRESETS[segmentIndex + 1].durationMs;
  const currentLog = Math.log(current);
  const nextLog = Math.log(next);
  const duration = Math.exp(currentLog + localT * (nextLog - currentLog));

  return clampDuration(duration);
}

export function getPresetMarkerValues() {
  return TIMELINE_PRESETS.map((preset) => ({
    id: preset.id,
    label: preset.label,
    durationMs: preset.durationMs,
    value: durationToSliderValue(preset.durationMs),
  }));
}

export function snapSliderValueToPreset(value, threshold = DEFAULT_SLIDER_SNAP_THRESHOLD) {
  const clampedValue = clampSliderValue(value);
  const nearest = getPresetMarkerValues().reduce((best, marker) => {
    const diff = Math.abs(clampedValue - marker.value);
    if (!best || diff < best.diff) return { marker, diff };
    return best;
  }, null);

  if (!nearest || nearest.diff > Math.max(0, threshold)) {
    return clampedValue;
  }
  return nearest.marker.value;
}

export function snapDurationToNearestPreset(durationMs) {
  const clampedDuration = clampDuration(durationMs);
  const nearestPreset = TIMELINE_PRESETS.reduce((best, preset) => {
    const diff = Math.abs(Math.log(clampedDuration / preset.durationMs));
    if (!best || diff < best.diff) return { preset, diff };
    return best;
  }, null);

  return nearestPreset?.preset.durationMs ?? TIMELINE_PRESETS[0].durationMs;
}

export function positionPctFromDate(dateMs, range) {
  const duration = rangeDurationMs(range);
  if (duration <= 0) return 0;
  return ((dateMs - range.start.getTime()) / duration) * 100;
}

export function dateFromPositionPct(positionPct, range) {
  const duration = rangeDurationMs(range);
  return new Date(range.start.getTime() + (positionPct / 100) * duration);
}

export function panRangeByMs(range, deltaMs) {
  return {
    start: new Date(range.start.getTime() + deltaMs),
    end: new Date(range.end.getTime() + deltaMs),
  };
}

export function zoomRangeAroundDate(range, direction, anchorDate) {
  const duration = rangeDurationMs(range);
  const nextDuration = clampDuration(duration * (direction > 0 ? 0.8 : 1.25));
  return setRangeDurationAroundDate(range, nextDuration, anchorDate);
}

export function setRangeDurationAroundDate(range, nextDurationMs, anchorDate) {
  const duration = rangeDurationMs(range);
  const nextDuration = clampDuration(nextDurationMs);
  const start = range.start.getTime();
  const anchor = anchorDate.getTime();
  const anchorRatio = duration > 0 ? (anchor - start) / duration : 0.5;
  const nextStart = anchor - nextDuration * anchorRatio;
  const nextEnd = nextStart + nextDuration;
  return {
    start: new Date(nextStart),
    end: new Date(nextEnd),
  };
}
